import React, { useState, useEffect } from 'react';
import { Phone, Mail, Check, X, Loader2, Eye, Wrench, CalendarDays, Car, User } from 'lucide-react';
import axios from 'axios';
import { API } from '../../constants';

// Booking Section - Customer reservation form
export const BookingSection = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [selectedType, setSelectedType] = useState('obhliadka');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    vehicle_info: '',
    notes: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (bookingEnabled) {
      fetchSlots();
    }
  }, [selectedType, bookingEnabled]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setBookingEnabled(response.data.booking_enabled);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/available-slots?slot_type=${selectedType}`);
      setSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      setStatus({ type: 'error', message: 'Vyberte termín' });
      return;
    }

    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/bookings`, {
        slot_id: selectedSlot.id,
        ...formData,
      });
      setStatus({ type: 'success', message: 'Rezervácia bola úspešne odoslaná! Čoskoro vás kontaktujeme.' });
      setFormData({ customer_name: '', customer_email: '', customer_phone: '', vehicle_info: '', notes: '' });
      setSelectedSlot(null);
      fetchSlots();
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.detail || 'Nepodarilo sa vytvoriť rezerváciu.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Group slots by date
  const slotsByDate = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  return (
    <section id="booking" data-testid="booking-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00BFFF] mb-4">
            Rezervácia
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium">
            Objednajte sa online
          </h2>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
            {bookingEnabled 
              ? 'Vyberte si termín, ktorý vám vyhovuje. Obhliadka je nezáväzná a bezplatná.'
              : 'Online rezervácie sú momentálne pozastavené. Kontaktujte nás telefonicky.'}
          </p>
        </div>

        {!bookingEnabled ? (
          <div className="max-w-xl mx-auto text-center">
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-8">
              <Phone className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium mb-2">Zavolajte nám</h3>
              <p className="text-zinc-400 mb-6">
                Online rezervácie sú dočasne vypnuté. Pre objednanie termínu nás kontaktujte telefonicky.
              </p>
              <a
                href="tel:+421915264064"
                className="inline-flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-8 py-4 rounded-lg font-semibold hover:bg-[#33CCFF] transition-all"
              >
                <Phone className="w-5 h-5" />
                +421 915 264 064
              </a>
            </div>
          </div>
        ) : (
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Slot Selection */}
          <div>
            {/* Type Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => { setSelectedType('obhliadka'); setSelectedSlot(null); }}
                className={`flex-1 py-4 rounded-xl font-medium transition-all ${
                  selectedType === 'obhliadka'
                    ? 'bg-[#00BFFF] text-[#05050A]'
                    : 'bg-white/5 border border-white/10 text-zinc-400 hover:bg-white/10'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <Eye className="w-5 h-5" />
                  <span>Obhliadka</span>
                  <span className="text-xs opacity-70">30 min, zdarma</span>
                </div>
              </button>
              <button
                onClick={() => { setSelectedType('realizacia'); setSelectedSlot(null); }}
                className={`flex-1 py-4 rounded-xl font-medium transition-all ${
                  selectedType === 'realizacia'
                    ? 'bg-[#00BFFF] text-[#05050A]'
                    : 'bg-white/5 border border-white/10 text-zinc-400 hover:bg-white/10'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <Wrench className="w-5 h-5" />
                  <span>Realizácia</span>
                  <span className="text-xs opacity-70">po dohode</span>
                </div>
              </button>
            </div>

            {/* Available Slots */}
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
                </div>
              ) : Object.keys(slotsByDate).length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Momentálne nie sú dostupné žiadne termíny.</p>
                  <p className="text-sm mt-2">Kontaktujte nás telefonicky.</p>
                </div>
              ) : (
                Object.entries(slotsByDate)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([date, dateSlots]) => (
                    <div key={date} className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CalendarDays className="w-4 h-4 text-[#00BFFF]" />
                        <span className="font-medium">
                          {new Date(date).toLocaleDateString('sk-SK', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dateSlots
                          .sort((a, b) => a.time.localeCompare(b.time))
                          .map(slot => (
                            <button
                              key={slot.id}
                              onClick={() => setSelectedSlot(slot)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                selectedSlot?.id === slot.id
                                  ? 'bg-[#00BFFF] text-[#05050A]'
                                  : 'bg-white/10 text-white hover:bg-white/20'
                              }`}
                            >
                              {slot.time}
                            </button>
                          ))}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

          {/* Booking Form */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <h3 className="text-xl font-medium mb-6">
              {selectedSlot 
                ? `Vybraný termín: ${new Date(selectedSlot.date).toLocaleDateString('sk-SK')} o ${selectedSlot.time}`
                : 'Vyberte termín z kalendára'
              }
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Meno a priezvisko *</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="text"
                    data-testid="booking-name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    placeholder="Vaše meno"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="email"
                    data-testid="booking-email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    placeholder="vas@email.sk"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Telefón *</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="tel"
                    data-testid="booking-phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    placeholder="+421 9XX XXX XXX"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Vozidlo (voliteľné)</label>
                <div className="relative">
                  <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="text"
                    data-testid="booking-vehicle"
                    value={formData.vehicle_info}
                    onChange={(e) => setFormData({ ...formData, vehicle_info: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    placeholder="Napr. BMW E90 2008"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Poznámka (voliteľné)</label>
                <textarea
                  data-testid="booking-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF] min-h-[80px] resize-none"
                  placeholder="Čo potrebujete riešiť?"
                />
              </div>

              {status.message && (
                <div className={`p-4 rounded-lg ${
                  status.type === 'success' 
                    ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {status.type === 'success' ? <Check size={18} /> : <X size={18} />}
                    {status.message}
                  </div>
                </div>
              )}

              <button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting || !selectedSlot}
                className="w-full py-4 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] hover:shadow-[0_0_20px_rgba(0,191,255,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CalendarDays className="w-5 h-5" />
                    Rezervovať termín
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
        )}
      </div>
    </section>
  );
};
